import { Link } from "@tanstack/react-router";
import { useT } from "@/lib/i18n";
import showroomImg from "@/assets/farve/showroom.jpg";
import collCeiling from "@/assets/farve/coll-ceiling.jpg";
import allGoods from "@/assets/farve/all-goods.jpg";

export const showroomHeadDa = () => ({
  meta: [
    { title: "Showroomet — Farveladeland" },
    {
      name: "description",
      content:
        "Besøg Farveladelands showroom i København efter aftale. Et varmt oplyst rum med vintage Murano-lamper og udvalgte loppefund.",
    },
    { property: "og:title", content: "Showroomet — Farveladeland" },
    {
      property: "og:description",
      content: "Showroom efter aftale i København for vintage Murano-glas.",
    },
    { property: "og:image", content: showroomImg },
  ],
});

export const showroomHeadEn = () => ({
  meta: [
    { title: "The Showroom — Farveladeland" },
    {
      name: "description",
      content:
        "Visit Farveladeland's Copenhagen showroom by appointment. A warmly lit room of vintage Murano lamps and curated flea-market finds.",
    },
    { property: "og:title", content: "The Showroom — Farveladeland" },
    {
      property: "og:description",
      content: "Copenhagen showroom for vintage Murano glass — by appointment.",
    },
    { property: "og:image", content: showroomImg },
  ],
});

export function ShowroomPage() {
  const { t } = useT();
  const info = [
    { k: t("showroom.info.address.k"), v: t("showroom.info.address.v") },
    { k: t("showroom.info.hours.k"), v: t("showroom.info.hours.v") },
    { k: t("showroom.info.dispatch.k"), v: t("showroom.info.dispatch.v") },
  ];

  return (
    <>
      <section className="relative h-[80svh] min-h-[520px] overflow-hidden">
        <img
          src={showroomImg}
          alt="Farveladelands showroom — en Murano-lampe på en dansk stol"
          className="absolute inset-0 h-full w-full object-cover"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/75 via-foreground/15 to-foreground/35" />
        <div className="relative mx-auto flex h-full max-w-[1500px] flex-col justify-end px-6 pb-16 md:px-10 md:pb-24">
          <p className="eyebrow text-background/85">{t("showroom.eyebrow")}</p>
          <h1 className="mt-4 font-display text-[clamp(3rem,8vw,7rem)] leading-[0.95] text-background">
            {t("showroom.titleA")}
            <br />
            <em className="text-background/80">{t("showroom.titleB")}</em>
          </h1>
        </div>
      </section>

      <section className="mx-auto max-w-[1500px] px-6 py-24 md:px-10 md:py-32">
        <div className="grid gap-16 md:grid-cols-12">
          <div className="md:col-span-5">
            <p className="eyebrow">{t("showroom.appt.eyebrow")}</p>
            <h2 className="mt-4 font-display text-4xl leading-tight md:text-5xl">
              {t("showroom.appt.title")}
            </h2>
          </div>
          <div className="space-y-6 text-base leading-relaxed text-muted-foreground md:col-span-7">
            <p>{t("showroom.appt.body1")}</p>
            <p>{t("showroom.appt.body2")}</p>
            <Link
              to="/visit"
              className="mt-4 inline-flex items-center gap-3 rounded-full bg-foreground px-6 py-3.5 text-xs font-medium uppercase tracking-[0.2em] text-background hover:bg-terracotta"
            >
              {t("showroom.appt.cta")} →
            </Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1500px] px-6 pb-32 md:px-10">
        <div className="grid gap-3 md:grid-cols-12">
          <div className="aspect-[4/5] overflow-hidden rounded-sm bg-muted md:col-span-5">
            <img src={collCeiling} alt="Murano loftlampe-kollektion" loading="lazy" decoding="async" className="h-full w-full object-cover" />
          </div>
          <div className="aspect-[16/10] overflow-hidden rounded-sm bg-muted md:col-span-7 md:aspect-auto">
            <img src={allGoods} alt="Overblik over alle varer" loading="lazy" decoding="async" className="h-full w-full object-cover" />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1500px] px-6 pb-32 md:px-10">
        <div className="grid gap-8 rounded-sm border border-border bg-card p-10 md:grid-cols-3 md:p-14">
          {info.map((b) => (
            <div key={b.k}>
              <p className="eyebrow">{b.k}</p>
              <p className="mt-3 whitespace-pre-line font-display text-2xl leading-snug text-foreground">
                {b.v}
              </p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
